import React from 'react'
import { useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import PropTypes from 'prop-types'
import { DataGrid } from '@mui/x-data-grid'
import dayjs from 'dayjs'
import Constants from '../../../../../constants/en.json'
import { IconButton } from '../../../../../components/mui/Core/index'
import { useApplicationList } from '../../../../../hooks/applications/application-list'
import { getDateTimeFormattedValue } from '../../../../../utils/string-utils'
import { DeleteForeverIcon, EditIcon } from '../../../../../components/mui/Icons'
import NoResultsFoundTableOverlay from '../../../../../components/NoResultsFoundTableOverlay/no-results-found-table-overlay'

function RoleManagementTable({
  filterBy,
  filterValue,
  searchByOptions,
  handleDeleteRoleModalOpen,
}) {
  const navigate = useNavigate()

  const applicationList = useApplicationList()
  const roles = useSelector((state) => state.roleManagement.viewRole.roles)
  const isFetchingRoles = useSelector((state) => state.roleManagement.viewRole.isFetchingRoles)

  const selectedSearchOption = searchByOptions.filter((option) => {
    return option.id === filterBy
  })
  const filterByDescription = selectedSearchOption[0]?.text || ''

  const handleEditRole = (row) => () => {
    const selectedRole = { ...row }
    navigate('/role-management/edit-role', {
      state: {
        isEditingRole: true,
        selectedRole,
      },
    })
  }
  const handleDeleteRole = (row) => () => {
    handleDeleteRoleModalOpen(row)
  }

  const rows = roles?.map((row) => ({
    id: row.roleName,
    code: row.roleName,
    description: row.roleDescription,
    application: row.appName,
    dateCreated: dayjs(row.createdAt).valueOf(),
    dateModified: dayjs(row.lastModified).valueOf(),
    modifiedBy: row.modifiedBy,
  }))

  const columns = [
    {
      field: 'code',
      headerName: Constants.roleManagement.ROLE_CODE,
      type: 'string',
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'description',
      headerName: Constants.roleManagement.ROLE_DESCRIPTION,
      type: 'string',
      minWidth: 190,
      flex: 1,
    },
    {
      field: 'application',
      headerName: Constants.roleManagement.APPLICATION,
      type: 'string',
      minWidth: 150,
      flex: 1,
      valueGetter: (value) => applicationList[value],
    },
    {
      field: 'dateCreated',
      headerName: Constants.roleManagement.DATE_CREATED,
      type: 'string',
      minWidth: 120,
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => getDateTimeFormattedValue(value),
    },
    {
      field: 'dateModified',
      headerName: Constants.roleManagement.DATE_MODIFIED,
      type: 'string',
      minWidth: 120,
      flex: 1,
      headerAlign: 'right',
      align: 'right',
      valueFormatter: (value) => getDateTimeFormattedValue(value),
    },
    {
      field: 'modifiedBy',
      headerName: Constants.roleManagement.MODIFIED_BY,
      type: 'string',
      minWidth: 180,
      flex: 1,
    },
    {
      field: 'Actions',
      headerName: Constants.roleManagement.ACTIONS,
      type: 'string',
      minWidth: 120,
      flex: 0.5,
      sortable: false,
      renderCell: (cellValues) => {
        return (
          <>
            <IconButton onClick={handleEditRole(cellValues?.row)}>
              <EditIcon color='action' />
            </IconButton>
            <IconButton onClick={handleDeleteRole(cellValues?.row)}>
              <DeleteForeverIcon color='error' />
            </IconButton>
          </>
        )
      },
    },
  ]
  const filterModel = {
    items: [{ field: filterBy, operator: 'contains', value: filterValue }],
  }
  const gridStyle = {
    minHeight: '160px',
    height: isFetchingRoles ? '400px' : 'auto',
  }

  return (
    <DataGrid
      sx={gridStyle}
      rows={rows}
      columns={columns}
      filterModel={filterModel}
      initialState={{
        pagination: {
          paginationModel: { pageSize: 25, page: 0 },
        },
        sorting: {
          sortModel: [{ field: 'dateModified', sort: 'desc' }],
        },
      }}
      loading={isFetchingRoles}
      pageSizeOptions={[25, 50]}
      spacing={1}
      sortingOrder={['asc', 'desc']}
      disableColumnFilter
      disableRowSelectionOnClick
      slotProps={{
        pagination: {
          component: 'div',
          showLastButton: true,
          showFirstButton: true,
        },
        noResultsOverlay: {
          filterByDescription,
          noResultsValue: filterValue,
        },
        loadingOverlay: {
          variant: 'skeleton',
          noRowsVariant: 'skeleton',
        },
      }}
      slots={{
        noResultsOverlay: NoResultsFoundTableOverlay,
      }}
    />
  )
}

RoleManagementTable.propTypes = {
  filterBy: PropTypes.string.isRequired,
  filterValue: PropTypes.string.isRequired,
  searchByOptions: PropTypes.arrayOf(PropTypes.objectOf(PropTypes.any)).isRequired,
  handleDeleteRoleModalOpen: PropTypes.func.isRequired,
}

export default RoleManagementTable
